import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrgUser } from '../../context/OrgUserProvider';
import { getS3Object } from '../../utils/functions';
import CourseEditorSidebar from './CourseEditorSidebar.js';
import CourseInfoTab from './CourseInfoTab.js';
import LessonTab from './LessonTab.js';
import MediaUploadTab from './MediaUploadTab.js';

const CourseEditor = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('course-info');
    const [courseInfo, setCourseInfo] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const { getAccessTokenSilently } = useAuth0();
    const { orgUser } = useOrgUser();

    useEffect(() => {
        fetchCourseData();
        fetchCategories();
    }, [courseId]);

    const fetchThumbnail = async (course, token, orgName) => {
        if (!course.thumbnail) {
            setThumbnailUrl(null);
            return;
        }

        try {
            const thumbnailResponse = await getS3Object(
                `QallixAcademy/SoftwareCourses/${courseId}/${course.thumbnail}`,
                'qallix-public',
                token,
                orgName
            );

            if (thumbnailResponse.statusCode === 200) {
                const url = `data:${thumbnailResponse.contentType};base64,${thumbnailResponse.fileContent}`;
                setThumbnailUrl(url);
            } else {
                setThumbnailUrl(null);
            }
        } catch (err) {
            console.error(`Failed to get thumbnail for ${courseId}:`, err);
            setThumbnailUrl(null);
        }
    };

    const fetchCourseData = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = await getAccessTokenSilently({
                audience: 'https://qallix-api',
            });
            const orgName = orgUser.orgName;

            const courseInfoResponse = await getS3Object(
                `QallixAcademy/SoftwareCourses/${courseId}/course-info.json`,
                'qallix-public',
                token,
                orgName
            );

            if (courseInfoResponse.statusCode === 200) {
                const decodedContent = atob(courseInfoResponse.fileContent);
                const courseData = JSON.parse(decodedContent);
                setCourseInfo(courseData);

                if (courseData.lessons && Array.isArray(courseData.lessons)) {
                    const sortedLessons = courseData.lessons.sort((a, b) =>
                        (a.order || 0) - (b.order || 0)
                    );
                    setLessons(sortedLessons);
                } else {
                    setLessons([]);
                }

                await fetchThumbnail(courseData, token, orgName);
            } else {
                throw new Error('Failed to fetch course information');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching course data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = await getAccessTokenSilently({
                audience: 'https://qallix-api',
            });
            const orgName = orgUser.orgName;

            const response = await getS3Object(
                'QallixAcademy/SoftwareCourses/category.json',
                'qallix-public',
                token,
                orgName
            );

            if (response.statusCode === 200) {
                const categoryData = JSON.parse(atob(response.fileContent));
                setCategories(categoryData.categories || []);
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const handleBackClick = () => {
        navigate('/manage-courses');
    };

    const renderTabContent = () => {
        if (activeTab === 'course-info') {
            return (
                <CourseInfoTab
                    courseId={courseId}
                    courseInfo={courseInfo}
                    thumbnailUrl={thumbnailUrl}
                    refreshCourseData={fetchCourseData}
                    setCourseInfo={setCourseInfo}
                    categories={categories}
                />
            );
        }

        if (activeTab.startsWith('lesson-')) {
            const lessonId = parseInt(activeTab.split('-')[1]);
            // Find the lesson by ID instead of index
            const lesson = lessons.find(l => l.id === lessonId);
            // Find the index for backward compatibility if needed
            const lessonIndex = lessons.findIndex(l => l.id === lessonId);

            return lesson ? (
                <LessonTab
                    lesson={lesson}
                    lessonIndex={lessonIndex}
                    courseId={courseId}
                />
            ) : (
                <Typography>Lesson not found</Typography>
            );
        }

        // Add this condition for the Media Upload tab
        if (activeTab === 'media-upload') {
            return <MediaUploadTab courseId={courseId} />;
        }

        return <Typography>Select a tab to view content</Typography>;
    };

    if (loading) {
        return (
            <Box sx={{ padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', fontFamily: "'Satoshi', sans-serif" }}>
                <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }}>Loading course information...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ padding: '20px', fontFamily: "'Satoshi', sans-serif" }}>
                <Box sx={{
                    padding: '10px 15px',
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontFamily: "'Satoshi', sans-serif",
                }}>
                    <Box sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                        Error: {error}
                    </Box>
                    <Button
                        onClick={fetchCourseData}
                        sx={{
                            color: '#332872',
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: 'rgba(51, 40, 114, 0.1)'
                            }
                        }}
                    >
                        Retry
                    </Button>
                </Box>
                <Button
                    onClick={handleBackClick}
                    startIcon={<ArrowBack />}
                    sx={{
                        color: '#332872',
                        fontFamily: "'Satoshi', sans-serif",
                        fontWeight: 'bold',
                    }}
                >
                    Back to Courses
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', fontFamily: "'Satoshi', sans-serif" }}>
            <Box sx={{
                display: 'flex',
                flex: 1,
                backgroundColor: '#f5f7fa',
                padding: '20px 20px 20px 0',
                fontFamily: "'Satoshi', sans-serif",
            }}>
                <CourseEditorSidebar
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    courseInfo={courseInfo}
                    lessons={lessons}
                    onBackClick={handleBackClick}
                />
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    marginLeft: '20px',
                    padding: '20px',
                    fontFamily: "'Satoshi', sans-serif",
                }}>
                    {renderTabContent()}
                </Box>
            </Box>
        </Box>
    );
};

export default CourseEditor;